// Google Places reviews for the Hotel Details page.
// Renders the top ~5 reviews with the aggregate rating and a "read all on
// Google" link. Fetches from our backend cache (24h). When the backend
// returns 404 (Places API disabled or no match) the section renders NOTHING —
// this lets the platform ship with Places disabled without any UI glitches.

import React, { useEffect, useState } from 'react';
import { Star, ExternalLink } from 'lucide-react';
import { api } from '../../lib/api';

const Stars = ({ value, size = 3.5 }) => {
  const v = Math.max(0, Math.min(5, Number(value) || 0));
  const full = Math.floor(v);
  const half = v - full >= 0.5;
  return (
    <div className="inline-flex items-center gap-0.5" aria-label={`${v.toFixed(1)} out of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-${size} w-${size} ${i < full ? 'text-yellow-500 fill-yellow-500' : i === full && half ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300 dark:text-gray-600'}`}
        />
      ))}
    </div>
  );
};

const HotelReviews = ({ hotel, onAvailable }) => {
  const [data, setData] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const lat = hotel?.coordinates?.latitude;
    const lng = hotel?.coordinates?.longitude;
    if (!hotel?.id || !hotel?.name || !lat || !lng) {
      setHidden(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api.get(`/hotels/reviews/${encodeURIComponent(hotel.id)}`, {
      params: { name: hotel.name, lat, lng },
    })
      .then(({ data: d }) => {
        if (cancelled) return;
        setData(d);
        if (d?.reviews?.length > 0 && typeof onAvailable === 'function') onAvailable(true);
      })
      .catch(() => { if (!cancelled) setHidden(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotel?.id, hotel?.name, hotel?.coordinates?.latitude, hotel?.coordinates?.longitude]);

  if (hidden || (!loading && (!data?.reviews || data.reviews.length === 0))) {
    return null;
  }

  if (loading) {
    return (
      <div className="space-y-3" data-testid="hotel-reviews-loading">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-20 rounded-lg bg-gray-100 dark:bg-gray-800 animate-pulse" />
        ))}
      </div>
    );
  }

  const place = data.place || {};
  const reviews = data.reviews || [];

  return (
    <div className="space-y-4" data-testid="hotel-reviews">
      {/* Aggregate row */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-3 py-1.5 rounded-lg">
            <p className="text-2xl font-bold leading-none">{Number(place.rating || 0).toFixed(1)}</p>
            <p className="text-[10px] uppercase tracking-widest mt-0.5">on Google</p>
          </div>
          <div>
            <Stars value={place.rating} />
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              Based on {Number(place.user_rating_count || 0).toLocaleString('en-IN')} Google reviews
            </p>
          </div>
        </div>
        {place.google_maps_uri && (
          <a
            href={place.google_maps_uri}
            target="_blank"
            rel="noreferrer"
            data-testid="hotel-reviews-all-link"
            className="inline-flex items-center gap-1 text-sm font-semibold text-red-600 hover:underline"
          >
            Read all on Google <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      {/* Review cards */}
      <div className="space-y-3">
        {reviews.slice(0, 5).map((r, idx) => (
          <article
            key={idx}
            data-testid={`hotel-review-${idx}`}
            className="p-4 rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900/60"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2 min-w-0">
                {r.author_photo ? (
                  <img
                    src={r.author_photo}
                    alt=""
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    className="h-7 w-7 rounded-full object-cover shrink-0"
                  />
                ) : (
                  <div className="h-7 w-7 rounded-full bg-gray-200 dark:bg-gray-700 grid place-items-center text-[11px] font-bold text-gray-600 dark:text-gray-300 shrink-0">
                    {(r.author || 'A').charAt(0).toUpperCase()}
                  </div>
                )}
                <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{r.author}</p>
              </div>
              <span className="shrink-0 inline-flex items-center gap-0.5 text-xs font-bold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 px-1.5 py-0.5 rounded">
                {Number(r.rating || 0).toFixed(1)} <Star className="h-3 w-3 fill-current" />
              </span>
            </div>
            {r.text && (
              <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line line-clamp-5">{r.text}</p>
            )}
            {r.relative_time && (
              <p className="text-[11px] text-gray-500 dark:text-gray-500 mt-2">{r.relative_time}</p>
            )}
          </article>
        ))}
      </div>
      <p className="text-[11px] text-gray-500 dark:text-gray-400">
        Reviews and ratings by Google. Refreshed every 24 hours.
      </p>
    </div>
  );
};

export default HotelReviews;
